import { create } from 'zustand';
import { message } from 'antd';
import apiCore from '../utils/api-core';
import { orderModules } from '../utils/module';
import { findInvalidMergeTokens } from '../utils/mailmerge';

const useTemplateCatalogStore = create((set, get) => ({
    // Data
    singleStepItems: [],
    ownedSequenceItems: [],
    multiStepItems: [],
    loading: false,
    filter: 'all',
    search: '',
    sort: 'newest',
    expandedModules: {}, // moduleGroupId → Module[]
    _pendingLoads: {},   // moduleGroupId → Promise (prevents duplicate API calls)

    // Setters
    setFilter: (filter) => set({ filter }),
    setSearch: (search) => set({ search }),
    setSort: (sort) => set({ sort }),

    // Fetch all sources in parallel: user templates, owned sequences, catalog sequences
    fetchCatalog: async () => {
        set({ loading: true });
        try {
            const [templatesRes, ownedSeqRes, catalogSeqRes] = await Promise.all([
                apiCore.get('/api/v1/templates'),
                apiCore.get('/api/v3/module-group', {
                    params: { scope: 'owned', limit: 100, sort: 'created_at', order: 'desc' }
                }),
                apiCore.get('/api/v3/module-group', {
                    params: { scope: 'catalog', limit: 100, sort: 'created_at', order: 'desc' }
                })
            ]);

            const templates = templatesRes.data || templatesRes;
            const ownedSeqs = ownedSeqRes.data || ownedSeqRes;
            const catalogSeqs = catalogSeqRes.data || catalogSeqRes;

            // Normalize single-step templates (these go into "My Templates")
            const singleStepItems = (Array.isArray(templates) ? templates : []).map(t => ({
                id: t.id,
                type: 'single-step',
                title: t.name,
                description: t.details,
                message: t.details,
                category: t.category || 'Uncategorized',
                stepCount: 1,
                source: 'user',
                owner: t.user,
                privacy: t.privacy,
                createdAt: t.created_at || t.createdAt || '',
                invalidMergeTokens: findInvalidMergeTokens(t.details),
                _raw: t,
            }));

            // Normalize owned sequences (these also go into "My Templates")
            const ownedRows = ownedSeqs.rows || [];
            const ownedSequenceItems = ownedRows.map(mg => ({
                id: mg.id,
                type: 'multi-step',
                title: mg.name,
                description: mg.initial_message_response
                    ? `Starts with: ${mg.initial_message_response.split('\n')[0]}`
                    : '',
                category: mg.category || 'Uncategorized',
                stepCount: parseInt(mg.modules_count) || 0,
                source: 'user',
                moduleGroupId: mg.id,
                owner: mg.owner_name ? { id: mg.user_id, name: mg.owner_name, avatar_url: mg.owner_avatar_url } : null,
                privacy: mg.privacy,
                createdAt: mg.created_at || '',
                invalidMergeTokens: findInvalidMergeTokens(mg.all_responses || ''),
            }));

            // Normalize catalog sequences (these go into "Catalog")
            const catalogRows = catalogSeqs.rows || [];
            const multiStepItems = catalogRows.map(mg => ({
                id: mg.id,
                type: 'multi-step',
                title: mg.name,
                description: mg.initial_message_response
                    ? `Starts with: ${mg.initial_message_response.split('\n')[0]}`
                    : '',
                category: mg.category || 'Uncategorized',
                stepCount: parseInt(mg.modules_count) || 0,
                source: 'catalog',
                moduleGroupId: mg.id,
                owner: mg.owner_name ? { id: mg.user_id, name: mg.owner_name, avatar_url: mg.owner_avatar_url } : null,
                createdAt: mg.created_at || '',
            }));

            set({ singleStepItems, ownedSequenceItems, multiStepItems, loading: false });
        } catch (error) {
            console.error('Failed to fetch catalog:', error);
            set({ loading: false });
            message.error('Failed to load template catalog');
        }
    },

    // Get "My Templates" items (single-step + owned sequences)
    getMyItems: () => {
        const { singleStepItems, ownedSequenceItems } = get();
        return [...singleStepItems, ...ownedSequenceItems];
    },

    // Get filtered + merged + sorted items
    getFilteredItems: () => {
        const { singleStepItems, ownedSequenceItems, multiStepItems, filter, search, sort } = get();
        let items = [];

        switch (filter) {
            case 'single-step':
                items = singleStepItems;
                break;
            case 'multi-step':
                items = [...ownedSequenceItems, ...multiStepItems];
                break;
            case 'all':
            default:
                items = [...multiStepItems, ...ownedSequenceItems, ...singleStepItems];
                break;
        }

        if (search) {
            const s = search.toLowerCase();
            items = items.filter(item => {
                const title = (item.title || '').toLowerCase();
                const desc = (item.description || '').toLowerCase();
                const cat = (item.category || '').toLowerCase();
                const ownerName = (item.owner?.name || '').toLowerCase();
                return title.includes(s) || desc.includes(s) || cat.includes(s) || ownerName.includes(s);
            });
        }

        // Sort
        items = [...items];
        switch (sort) {
            case 'name':
                items.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                break;
            case 'steps':
                items.sort((a, b) => (b.stepCount || 0) - (a.stepCount || 0));
                break;
            case 'newest':
            default:
                items.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
                break;
        }

        return items;
    },

    // Lazy-load modules for a multi-step card (with in-flight dedup)
    loadModuleDetails: async (moduleGroupId) => {
        const { expandedModules, _pendingLoads } = get();
        if (expandedModules[moduleGroupId]) {
            return expandedModules[moduleGroupId];
        }
        if (_pendingLoads[moduleGroupId]) {
            return _pendingLoads[moduleGroupId];
        }

        const promise = apiCore.get(`/api/v3/module-group/${moduleGroupId}`)
            .then(res => {
                const data = res.data || res;
                const startModuleId = data.module_id || null;
                const rawModules = data.modules || [];
                const modules = startModuleId ? orderModules(startModuleId, rawModules) : rawModules;
                set(state => ({
                    expandedModules: { ...state.expandedModules, [moduleGroupId]: modules },
                    _pendingLoads: { ...state._pendingLoads, [moduleGroupId]: undefined },
                }));
                return modules;
            })
            .catch(error => {
                console.error('Failed to load module details:', error);
                set(state => ({
                    _pendingLoads: { ...state._pendingLoads, [moduleGroupId]: undefined },
                }));
                message.error('Failed to load sequence steps');
                return [];
            });

        set(state => ({
            _pendingLoads: { ...state._pendingLoads, [moduleGroupId]: promise },
        }));
        return promise;
    },

    // Duplicate a multi-step template
    duplicateTemplate: async (moduleGroupId, name, description, category, { privacy } = {}) => {
        try {
            await apiCore.post(`/api/v3/module-group/${moduleGroupId}/duplicate`, {
                name: `${name} (Copy)`,
                description: description || null,
                category: category || null,
                ...(privacy ? { privacy } : {}),
            });
            message.success('Duplicated, find it on your templates list.');
            // Refresh so the duplicate appears in "My Templates"
            get().fetchCatalog();
        } catch (error) {
            console.error('Failed to duplicate template:', error);
            message.error('Failed to duplicate sequence');
        }
    },

    // ── Catalog Library (tree browser) ────────────────────────────
    catalogTemplates: [],       // single-message catalog templates from /api/v3/templates/catalog
    catalogSequences: [],       // multi-step catalog sequences
    categories: [],             // [{ category, templateCount, sequenceCount }]
    selectedCategory: null,     // e.g. 'recruiting/candidate-outreach'
    catalogSearch: '',
    catalogTypeFilter: 'all',   // 'all' | 'message' | 'sequence'
    catalogLoading: false,

    setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
    setCatalogSearch: (catalogSearch) => set({ catalogSearch }),
    setCatalogTypeFilter: (catalogTypeFilter) => set({ catalogTypeFilter }),

    fetchCatalogLibrary: async () => {
        set({ catalogLoading: true });
        try {
            const [catalogTplRes, catalogSeqRes, categoriesRes] = await Promise.all([
                apiCore.get('/api/v3/templates/catalog', { params: { limit: 100 } }),
                apiCore.get('/api/v3/module-group', {
                    params: { scope: 'catalog', limit: 100, sort: 'created_at', order: 'desc' }
                }),
                apiCore.get('/api/v3/templates/categories'),
            ]);

            const tplData = catalogTplRes.data || catalogTplRes;
            const seqData = catalogSeqRes.data || catalogSeqRes;
            const catData = categoriesRes.data || categoriesRes;

            // Normalize single-message catalog templates
            const tplRows = tplData.rows || (Array.isArray(tplData) ? tplData : []);
            const catalogTemplates = tplRows.map(t => ({
                id: t.id,
                type: 'single-step',
                title: t.name,
                description: t.details,
                message: t.details,
                category: t.category || 'Uncategorized',
                stepCount: 1,
                source: 'catalog',
                createdAt: t.created_at || t.createdAt || '',
                _raw: t,
            }));

            // Normalize catalog sequences
            const seqRows = seqData.rows || [];
            const catalogSequences = seqRows.map(mg => ({
                id: mg.id,
                type: 'multi-step',
                title: mg.name,
                description: mg.description || (mg.initial_message_response
                    ? `Starts with: ${mg.initial_message_response.split('\n')[0]}`
                    : ''),
                category: mg.category || 'Uncategorized',
                stepCount: parseInt(mg.modules_count) || 0,
                source: 'catalog',
                moduleGroupId: mg.id,
                createdAt: mg.created_at || '',
            }));

            const categories = Array.isArray(catData) ? catData : [];

            set({ catalogTemplates, catalogSequences, categories, catalogLoading: false });
        } catch (error) {
            console.error('Failed to fetch catalog library:', error);
            set({ catalogLoading: false });
            message.error('Failed to load template library');
        }
    },

    // Get catalog items filtered by selected category, search, and type
    getCatalogItemsByCategory: () => {
        const {
            catalogTemplates, catalogSequences,
            selectedCategory, catalogSearch, catalogTypeFilter
        } = get();

        let items = [...catalogTemplates, ...catalogSequences];

        // Filter by category
        if (selectedCategory) {
            items = items.filter(item => {
                // If a section key is selected (e.g. 'recruiting'), match all subcategories
                if (!selectedCategory.includes('/')) {
                    return item.category.startsWith(selectedCategory + '/');
                }
                return item.category === selectedCategory;
            });
        }

        // Filter by type
        if (catalogTypeFilter === 'message') {
            items = items.filter(item => item.type === 'single-step');
        } else if (catalogTypeFilter === 'sequence') {
            items = items.filter(item => item.type === 'multi-step');
        }

        // Filter by search
        if (catalogSearch) {
            const s = catalogSearch.toLowerCase();
            items = items.filter(item => {
                const title = (item.title || '').toLowerCase();
                const desc = (item.description || '').toLowerCase();
                return title.includes(s) || desc.includes(s);
            });
        }

        // Sort: sequences first, then alphabetical
        items.sort((a, b) => {
            if (a.type !== b.type) return a.type === 'multi-step' ? -1 : 1;
            return (a.title || '').localeCompare(b.title || '');
        });

        return items;
    },

    // Fetch enrollment count for a sequence
    fetchEnrollmentCount: async (moduleGroupId) => {
        try {
            const res = await apiCore.get(`/api/v3/module-group/${moduleGroupId}/enrollment-count`);
            const data = res.data || res;
            return data.enrollmentCount || 0;
        } catch (error) {
            console.error('Failed to fetch enrollment count:', error);
            return 0;
        }
    },

    // Delete a sequence (module group)
    deleteSequence: async (moduleGroupId) => {
        await apiCore.delete(`/api/v3/module-group/${moduleGroupId}`);
        get().fetchCatalog();
    },

    // Copy a single-message template to the user's own templates
    copyTemplateToUser: async (item, { privacy = 'public', nameSuffix = '' } = {}) => {
        try {
            await apiCore.post('/api/v1/templates', {
                name: `${item.title || item.name}${nameSuffix}`,
                details: item.message || item.description || item.details,
                category: item.category || 'Uncategorized',
                privacy,
            });
            message.success('Template copied to your templates.');
            get().fetchCatalog();
        } catch (error) {
            console.error('Failed to copy template:', error);
            message.error('Failed to copy template');
        }
    },
}));

export default useTemplateCatalogStore;
